import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Search, Plus, Sun, Moon, Import, GraduationCap, BookOpen } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './components/Logo';
import WordList from './components/WordList';
import Statistics from './components/Statistics';
import AddWordForm from './components/AddWordForm';
import ImportModal from './components/ImportModal';
import StudyMode from './components/StudyMode';
import BlogPage from './pages/Blog';
import BlogEdit from './pages/BlogEdit';
import BlogGeneratorPage from './pages/BlogGenerator';
import Footer from './components/Footer';
import { Word, Theme, BlogPost } from './types';
import { db } from './services/db';
import { samplePosts } from './data/samplePosts';

function App() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>('light');
  const [words, setWords] = useState<Word[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(samplePosts);
  const [isStudyMode, setIsStudyMode] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await db.initialize();
        const storedWords = await db.words.toArray();
        setWords(storedWords);
        
        const storedPosts = await db.blogPosts.toArray();
        if (storedPosts.length === 0) {
          await db.blogPosts.bulkAdd(samplePosts);
          setBlogPosts(samplePosts);
        } else {
          setBlogPosts(storedPosts);
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };

    initializeApp();
  }, []);

  const filteredWords = words.filter(word => 
    word.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
    word.definition.toLowerCase().includes(searchQuery.toLowerCase()) ||
    word.source?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddWord = async (word: Word) => {
    try {
      const id = await db.words.add(word);
      setWords([...words, { ...word, id }]);
      setShowAddForm(false);
      toast.success('Word added successfully!');
    } catch (error) {
      toast.error('Failed to add word');
    }
  };

  const handleImport = async (data: string) => {
    try {
      const importedWords = JSON.parse(data);
      await db.words.bulkAdd(importedWords);
      setWords([...words, ...importedWords]);
      setShowImportModal(false);
      toast.success('Words imported successfully!');
    } catch (error) {
      toast.error('Failed to import words');
    }
  };

  const handleUpdateWord = async (updatedWord: Word) => {
    try {
      await db.words.put(updatedWord);
      setWords(words.map(w => w.id === updatedWord.id ? updatedWord : w));
      toast.success('Word updated successfully!');
    } catch (error) {
      toast.error('Failed to update word');
    }
  };

  const handleDeleteWord = async (wordId: string) => {
    try {
      await db.words.delete(wordId);
      setWords(words.filter(w => w.id !== wordId));
      toast.success('Word deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete word');
    }
  };

  const handleSaveBlogPost = async (post: BlogPost) => {
    try {
      if (post.id) {
        await db.blogPosts.put(post);
        setBlogPosts(prev => prev.map(p => p.id === post.id ? post : p));
      } else {
        const id = await db.blogPosts.add(post);
        setBlogPosts(prev => [{ ...post, id }, ...prev]);
      }
      navigate('/blog');
      toast.success('Blog post saved successfully!');
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const handleDeleteBlogPost = async (postId: string) => {
    try {
      await db.blogPosts.delete(postId);
      setBlogPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Blog post deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-purple-900 text-white' 
        : 'bg-gradient-to-br from-purple-100 via-white to-purple-200'
    }`}>
      <Toaster position="top-right" />
      
      <Routes>
        <Route path="/blog" element={
          <BlogPage 
            theme={theme} 
            posts={blogPosts}
            onDeletePost={handleDeleteBlogPost}
          />
        } />
        <Route path="/blog/edit/:id" element={
          <BlogEdit
            theme={theme}
            post={blogPosts.find(p => p.id === window.location.pathname.split('/').pop())}
            onSave={handleSaveBlogPost}
          />
        } />
        <Route path="/blog/new" element={
          <BlogEdit
            theme={theme}
            onSave={handleSaveBlogPost}
          />
        } />
        <Route path="/blog/generate" element={
          <BlogGeneratorPage
            words={words}
            theme={theme}
            onPostGenerated={handleSaveBlogPost}
          />
        } />
        <Route path="/" element={
          <div className="container mx-auto px-4 py-8">
            <header className="text-center mb-12">
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
                  className={`p-2 rounded-full ${
                    theme === 'dark' 
                      ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                      : 'bg-purple-100 text-purple-900 hover:bg-purple-200'
                  }`}
                >
                  {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center"
              >
                <div className="mb-4">
                  <Logo theme={theme} size="large" />
                </div>
                <h1 className={`text-5xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-purple-900'
                }`}>
                  LinguaTracker
                </h1>
                <h2 className={`text-2xl font-semibold mb-4 ${
                  theme === 'dark' ? 'text-purple-300' : 'text-purple-700'
                }`}>
                  Atẹ̀lé-èdè
                </h2>
                <p className={theme === 'dark' ? 'text-purple-200' : 'text-purple-600'}>
                  Expand your vocabulary, one word at a time
                </p>
              </motion.div>
            </header>

            <Statistics words={words} theme={theme} />

            <div className="flex flex-wrap gap-4 justify-center my-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddForm(true)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg flex items-center gap-2 hover:bg-purple-700"
              >
                <Plus size={20} />
                Add Word
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsStudyMode(true)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
              >
                <GraduationCap size={20} />
                Study Mode
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowImportModal(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700"
              >
                <Import size={20} />
                Import
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/blog')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"
              >
                <BookOpen size={20} />
                Blog
              </motion.button>
            </div>

            <div className="mb-8">
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search words, definitions, or sources..."
                    className={`w-full pl-10 pr-4 py-3 rounded-lg ${
                      theme === 'dark'
                        ? 'bg-gray-800 text-white placeholder-gray-400 border-gray-700'
                        : 'bg-white text-gray-900 placeholder-gray-500 border-gray-200'
                    }`}
                  />
                </div>
              </div>
            </div>

            <WordList
              words={filteredWords}
              onDelete={handleDeleteWord}
              onEdit={handleUpdateWord}
              theme={theme}
            />

            <AnimatePresence>
              {showAddForm && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                >
                  <AddWordForm
                    onAdd={handleAddWord}
                    onCancel={() => setShowAddForm(false)}
                    theme={theme}
                  />
                </motion.div>
              )}

              {showImportModal && (
                <ImportModal
                  onImport={handleImport}
                  onClose={() => setShowImportModal(false)}
                  theme={theme}
                />
              )}

              {isStudyMode && words.length > 0 && (
                <StudyMode
                  words={words}
                  onUpdateWord={handleUpdateWord}
                  theme={theme}
                  onClose={() => setIsStudyMode(false)}
                />
              )}
            </AnimatePresence>
          </div>
        } />
      </Routes>

      <Footer theme={theme} />
    </div>
  );
}

export default App;